import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Register.css';

function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    password: '',
    cpf: '',
    nome: '',
    apelido: '',
    telefone: '',
    foto: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Validações
  const validateCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf.length !== 11) return false;
    if (/^(\d)\1+$/.test(cpf)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(9))) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(10))) return false;

    return true;
  };

  const validatePhone = (phone) => {
    return /^\(\d{2}\) 9-\d{4}-\d{4}$/.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};
    if (step === 1 && (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))) {
      newErrors.email = 'E-mail inválido';
    }
    if (step === 2 && !formData.code) {
      newErrors.code = 'Código é obrigatório';
    }
    if (step === 3) {
      if (!formData.password || formData.password.length < 6) newErrors.password = 'A senha deve ter pelo menos 6 caracteres';
      if (!formData.cpf || !validateCPF(formData.cpf)) newErrors.cpf = 'CPF inválido';
      if (!formData.nome) newErrors.nome = 'Nome é obrigatório';
      if (!formData.apelido) newErrors.apelido = 'Apelido é obrigatório';
      if (!formData.telefone || !validatePhone(formData.telefone)) newErrors.telefone = 'Telefone inválido (formato: (99) 9-9999-9999)';
    }
    return newErrors;
  };

  // Handlers
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setFormData({ ...formData, [name]: file });
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPreviewImage(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPreviewImage(null);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleCPFChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove tudo que não é número
    if (value.length > 11) value = value.slice(0, 11); // Limita a 11 dígitos
    value = value
      .replace(/(\d{3})(\d)/, '$1.$2') // Adiciona o primeiro ponto
      .replace(/(\d{3})(\d)/, '$1.$2') // Adiciona o segundo ponto
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Adiciona o traço
    setFormData({ ...formData, cpf: value });
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length >= 3 && value[2] !== '9') value = value.slice(0, 2) + '9' + value.slice(2);
    value = value
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{1})(\d{4})/, '$1-$2')
      .replace(/(\d{4})(\d{4})$/, '$1-$2');
    setFormData({ ...formData, telefone: value });
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const logErrorToServer = async (error) => {
    try {
      const errorData = {
        message: error.message || 'Erro desconhecido',
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        } : null,
        url: `${API_BASE_URL}/wp-json/palpitex/v1/users`,
        timestamp: new Date().toISOString(),
      };
      await axios.post(`${API_BASE_URL}/wp-json/palpitex/v1/log-error`, errorData, {
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (logError) {
      console.error('Falha ao enviar erro para o servidor:', logError);
    }
  };

  const handleNextStep = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      if (step === 1) {
        await axios.post(`${API_BASE_URL}/wp-json/palpitex/v1/send-verification-code`, {
          email: formData.email,
        });
        setStep(2);
      } else if (step === 2) {
        const response = await axios.post(`${API_BASE_URL}/wp-json/palpitex/v1/verify-code`, {
          email: formData.email,
          code: formData.code,
        });
        if (response.data.valid) {
          setStep(3);
        } else {
          setErrors({ code: 'Código inválido' });
        }
      }
    } catch (error) {
      console.error('Erro na etapa:', error.response || error.message);
      await logErrorToServer(error);
      setErrors({ general: error.response?.data?.message || 'Erro ao processar a etapa' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append('username', formData.apelido);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('password', formData.password);
    formDataToSend.append('cpf', formData.cpf);
    formDataToSend.append('nome', formData.nome);
    formDataToSend.append('apelido', formData.apelido);
    formDataToSend.append('telefone', formData.telefone);
    if (formData.foto) formDataToSend.append('foto', formData.foto);

    try {
      const response = await axios.post(`${API_BASE_URL}/wp-json/palpitex/v1/users`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userRole', response.data.role);
      navigate('/campeonatos');
    } catch (error) {
      console.error('Erro na requisição:', error.response || error.message);
      await logErrorToServer(error);
      setErrors({ general: error.response?.data?.message || 'Erro ao cadastrar usuário' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h2>Cadastro</h2>
      <form onSubmit={step === 3 ? handleSubmit : handleNextStep}>
        {step === 1 && (
          <div>
            <input
              type="email"
              name="email"
              placeholder="E-mail"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            {errors.email && <p className="error">{errors.email}</p>}
          </div>
        )}
        {step === 2 && (
          <>
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled={true}
              />
            </div>
            <div>
              <input
                type="text"
                name="code"
                placeholder="Código de verificação"
                value={formData.code}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.code && <p className="error">{errors.code}</p>}
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled={true}
              />
            </div>
            <div>
              <input
                type="password"
                name="password"
                placeholder="Senha"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.password && <p className="error">{errors.password}</p>}
            </div>
            <div>
              <input
                type="text"
                name="cpf"
                placeholder="CPF (123.456.789-00)"
                value={formData.cpf}
                onChange={handleCPFChange}
                maxLength="14"
                disabled={loading}
              />
              {errors.cpf && <p className="error">{errors.cpf}</p>}
            </div>
            <div className="field-group-with-icon">
              <div className="field-group">
                <div>
                  <input
                    type="text"
                    name="nome"
                    placeholder="Nome completo"
                    value={formData.nome}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.nome && <p className="error">{errors.nome}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    name="apelido"
                    placeholder="Apelido (será seu nome de usuário)"
                    value={formData.apelido}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.apelido && <p className="error">{errors.apelido}</p>}
                </div>
              </div>
              <img
                width="75"
                src={previewImage || `${process.env.PUBLIC_URL}/images/sem-foto.jpeg`}
                alt="Foto Perfil"
                className="foto-perfil"
                onClick={handleImageClick}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div>
              <input
                type="tel"
                name="telefone"
                placeholder="Telefone (99) 9-9999-9999"
                value={formData.telefone}
                onChange={handlePhoneChange}
                maxLength="15"
                inputMode="numeric"
                disabled={loading}
              />
              {errors.telefone && <p className="error">{errors.telefone}</p>}
            </div>
            <input
              type="file"
              name="foto"
              accept="image/*"
              capture="environment"
              onChange={handleChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </>
        )}
        {errors.general && <p className="error">{errors.general}</p>}
        <button type="submit" disabled={loading}>
          {loading ? 'Carregando...' : step === 3 ? 'Cadastrar' : 'Avançar'}
        </button>
      </form>
    </div>
  );
}

export default Register;